// @flow

import * as React from 'react';

import { Link } from 'react-router-dom';

import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { toastr } from 'app/common/toastr';
import shallowEqual from 'app/lib/shallow-equal';
import i18n from 'app/common/i18n';

import ConfirmDialog from 'app/common/ConfirmDialog';

type Props = {
  item: mixed,
  rowProps: {
    onAuditOpen: () => void,
    onDuplicate: () => void,
    toggleActivation: () => void,
    reject: () => void,
    onExtendDwelling: () => void
  }
};

type State = {
  anchorEl: mixed | null,
  reason: string
};

class CellContextMenu extends React.Component<Props, State> {
  i18n: (string) => string;

  state = {
    anchorEl: null,
    reason: ''
  };

  shouldComponentUpdate(nextProps, nextState) {
    return !(
      shallowEqual(this.props, nextProps) && shallowEqual(this.state, nextState)
    );
  }

  handleDuplicate = () => {
    this.handleMenuClose();

    const { item } = this.props;
    const id = item.id;
    const address = (
      item.location.address || this.props.i18n('(no address)')
    ).replace(/, [0-9]{4} [A-ZÆØÅ].*/, '');
    this.props.rowProps.onDuplicate({ id, address });
  };

  toggleActivation = async () => {
    this.handleMenuClose();
    const { item } = this.props;
    await this.props.rowProps.toggleActivation(item.id, !item.active);
    await this.props.rowProps.refetch();
  };

  toggleRejection = async () => {
    this.handleMenuClose();
    const { item } = this.props;
    await this.props.rowProps.reject(item.id, { reason: this.state.reason });
    await this.props.rowProps.refetch();
  };

  showAudit = () => {
    this.handleMenuClose();
    this.props.rowProps.onAuditOpen(this.props.item.id);
  };

  handleMenuOpen = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  };

  onChangeHandler = (ev) => {
    const { name, value } = ev.currentTarget;
    this.setState({ [name]: value });
  };

  extendDwelling = async () => {
    this.handleMenuClose();
    const { item } = this.props;
    await this.props.rowProps.onExtendDwelling({
      dwellingId: item.id
    });
    toastr.success(
      this.props.i18n('Vi har noteret, at dit lejemål stadig er ledigt')
    );
  };

  render() {
    const { admin, item, column } = this.props;
    const id = item.id;
    const menuOpen = Boolean(this.state.anchorEl);

    return (
      <TableCell
        key={column.id}
        padding={column.disablePadding ? 'none' : column.padding || 'normal'}
        align={column.align}
        style={{
          width: 48,
          textAlign: 'right',
          fontSize: '0.8em'
        }}
      >
        <IconButton
          aria-label="More"
          aria-controls={menuOpen ? 'long-menu' : undefined}
          aria-haspopup="true"
          onClick={this.handleMenuOpen}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          autoFocus={false}
          disableAutoFocusItem
          id="long-menu"
          anchorEl={this.state.anchorEl}
          open={menuOpen}
          onClose={this.handleMenuClose}
        >
          {admin || (item.active && item.status !== 'APPROVED') ? (
            <MenuItem
              dense
              component={Link}
              to={`/dwellings/${id}`}
              key="edit"
              onClick={this.handleMenuClose}
            >
              <ListItemText primary={this.props.i18n('Edit')} />
            </MenuItem>
          ) : null}
          {item.status === 'APPROVED' && item.active === true ? (
            <MenuItem dense key="extendDwelling" onClick={this.extendDwelling}>
              <ListItemText primary={this.props.i18n('Stadig ledigt')} />
            </MenuItem>
          ) : null}
          <MenuItem
            dense
            component={Link}
            to={`/dwellings/${id}/preview`}
            key="review"
            onClick={this.handleMenuClose}
          >
            <ListItemText primary={this.props.i18n('Review')} />
          </MenuItem>
          <ConfirmDialog
            component={MenuItem}
            key="activation"
            onConfirm={this.toggleActivation}
            buttonProps={{
              dense: true,
              children: (
                <ListItemText
                  primary={
                    item.active
                      ? this.props.i18n('Deactivate')
                      : this.props.i18n('Activate')
                  }
                />
              )
            }}
            dialogProps={{
              title: item.active
                ? this.props.i18n('Deactivate')
                : this.props.i18n('Activate')
            }}
          />
          <MenuItem dense key="duplicate" onClick={this.handleDuplicate}>
            <ListItemText primary={this.props.i18n('Duplicate')} />
          </MenuItem>
          {admin ? <Divider /> : null}
          {admin && item.status !== 'REJECTED' && (
            <ConfirmDialog
              component={MenuItem}
              key="rejection"
              onConfirm={this.toggleRejection}
              buttonProps={{
                dense: true,
                children: <ListItemText primary={this.props.i18n('Reject')} />
              }}
              dialogProps={{
                title: this.props.i18n('Reject listing'),
                content: () => (
                  <TextField
                    name="reason"
                    helperText={this.props.i18n('Comments for rejection')}
                    multiline
                    fullWidth
                    onChange={this.onChangeHandler}
                  />
                )
              }}
            />
          )}
          {admin ? (
            <MenuItem dense key="audit" onClick={this.showAudit}>
              <ListItemText primary={'Log'} />
            </MenuItem>
          ) : null}
        </Menu>
      </TableCell>
    );
  }
}

export default i18n(CellContextMenu);